/**
 * Effect picker — plan §4's "apply a buff/debuff from a quick picker (name,
 * kind, duration) in under 5 seconds", with five people waiting.
 *
 * Open to players as well as the GM (plan §8 ruling 4); the server
 * validates regardless. Durations are plan §5's common set, composed into
 * the shared ScheduledExpiry shape here so the reducer never sees a UI
 * concept:
 *   until-start  → anchor target, startOfTurn, 1   ("until their next turn starts")
 *   until-end    → anchor target, endOfTurn, 1
 *   rounds (N≥1) → anchor target, endOfTurn, N     (0 is malformed by ruling)
 *   end-of-round → endOfRound, 1                    (anchor irrelevant, EFX-06)
 *   until-removed→ "untilRemoved"
 *
 * "Cast by" matters beyond flavour: the effect's source token decides the
 * APPLIER arm of the three-party removal rule (decision 9), so a player who
 * doesn't set it can't later remove their own cast from someone else's
 * token. Defaults to the caster's first owned token.
 *
 * Pure render + callbacks, idempotent, no net/ coupling.
 */

import type { EffectKind, ScheduledExpiry, Token } from "@asarimitsuha/table-core";

export interface EffectPickerContext {
  tokens: Record<string, Token>;
  /** The viewer's seat, for the cast-by default. */
  ownSeatId: string | null;
}

export interface EffectSpec {
  name: string;
  kind: EffectKind;
  targetTokenId: string;
  sourceTokenId: string | null;
  expiry: ScheduledExpiry | "untilRemoved";
}

export interface EffectPickerCallbacks {
  onApply(spec: EffectSpec): void;
}

export type DurationChoice =
  | "until-start"
  | "until-end"
  | "rounds"
  | "end-of-round"
  | "until-removed";

const DURATION_LABELS: Record<DurationChoice, string> = {
  "until-start": "Until their next turn starts",
  "until-end": "Until their next turn ends",
  rounds: "For N rounds",
  "end-of-round": "Until end of round",
  "until-removed": "Until removed",
};

/** Compose the shared expiry from a duration choice (exported for tests —
 *  this mapping IS the picker's logic). */
export function composeExpiry(
  choice: DurationChoice,
  targetTokenId: string,
  rounds: number,
): ScheduledExpiry | "untilRemoved" {
  switch (choice) {
    case "until-start":
      return { anchorTokenId: targetTokenId, phase: "startOfTurn", roundsRemaining: 1 };
    case "until-end":
      return { anchorTokenId: targetTokenId, phase: "endOfTurn", roundsRemaining: 1 };
    case "rounds":
      return {
        anchorTokenId: targetTokenId,
        phase: "endOfTurn",
        // 0 (and NaN, and 2.7) must never reach the wire — malformed by ruling.
        roundsRemaining: Number.isFinite(rounds) ? Math.max(1, Math.floor(rounds)) : 1,
      };
    case "end-of-round":
      return { anchorTokenId: targetTokenId, phase: "endOfRound", roundsRemaining: 1 };
    case "until-removed":
      return "untilRemoved";
  }
}

function sortedTokens(tokens: Record<string, Token>): Token[] {
  return Object.values(tokens).sort(
    (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
  );
}

function tokenSelect(
  testid: string,
  tokens: readonly Token[],
  noneLabel: string | null,
): HTMLSelectElement {
  const select = document.createElement("select");
  select.dataset["testid"] = testid;
  if (noneLabel !== null) {
    const none = document.createElement("option");
    none.value = "";
    none.textContent = noneLabel;
    select.appendChild(none);
  }
  for (const token of tokens) {
    const option = document.createElement("option");
    option.value = token.id;
    option.textContent = token.name;
    select.appendChild(option);
  }
  return select;
}

function labelled(text: string, control: HTMLElement): HTMLLabelElement {
  const label = document.createElement("label");
  label.textContent = text;
  label.appendChild(control);
  return label;
}

/**
 * Render the picker into `root`. Idempotent. Renders nothing when the room
 * has no tokens (nothing to target — an empty form would be a trap).
 */
export function renderEffectPicker(
  root: HTMLElement,
  ctx: EffectPickerContext,
  cb: EffectPickerCallbacks,
): void {
  root.replaceChildren();
  const tokens = sortedTokens(ctx.tokens);
  if (tokens.length === 0) return;

  const form = document.createElement("form");
  form.dataset["testid"] = "effect-picker";

  const heading = document.createElement("h3");
  heading.textContent = "Apply effect";
  form.appendChild(heading);

  const name = document.createElement("input");
  name.dataset["testid"] = "effect-name";
  name.name = "effectName";
  name.required = true;
  name.maxLength = 40;
  name.placeholder = "Poison, Blessing…";
  form.appendChild(labelled("Effect name", name));

  const kind = document.createElement("select");
  kind.dataset["testid"] = "effect-kind";
  for (const value of ["debuff", "buff"] as const) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = value === "debuff" ? "Debuff" : "Buff";
    kind.appendChild(option);
  }
  form.appendChild(labelled("Kind", kind));

  const target = tokenSelect("effect-target", tokens, null);
  form.appendChild(labelled("On", target));

  // Cast-by: the applier arm of removal rights rides on this.
  const source = tokenSelect("effect-source", tokens, "— nobody / environment —");
  const own = tokens.find((t) => t.ownerSeatId !== null && t.ownerSeatId === ctx.ownSeatId);
  if (own) source.value = own.id;
  form.appendChild(labelled("Cast by", source));

  const duration = document.createElement("select");
  duration.dataset["testid"] = "effect-duration";
  for (const [value, text] of Object.entries(DURATION_LABELS)) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = text;
    duration.appendChild(option);
  }
  form.appendChild(labelled("Duration", duration));

  const rounds = document.createElement("input");
  rounds.dataset["testid"] = "effect-rounds";
  rounds.type = "number";
  rounds.min = "1";
  rounds.step = "1";
  rounds.value = "2";
  const roundsLabel = labelled("Rounds", rounds);
  roundsLabel.hidden = true; // only meaningful for the "rounds" choice
  form.appendChild(roundsLabel);
  duration.addEventListener("change", () => {
    roundsLabel.hidden = duration.value !== "rounds";
  });

  const apply = document.createElement("button");
  apply.type = "submit";
  apply.dataset["testid"] = "apply-effect";
  apply.textContent = "Apply";
  form.appendChild(apply);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const effectName = name.value.trim();
    if (effectName === "" || target.value === "") return;
    cb.onApply({
      name: effectName,
      kind: kind.value === "buff" ? "buff" : "debuff",
      targetTokenId: target.value,
      sourceTokenId: source.value === "" ? null : source.value,
      expiry: composeExpiry(
        duration.value as DurationChoice,
        target.value,
        Number(rounds.value),
      ),
    });
    name.value = ""; // ready for the next one — the five people are waiting
  });

  root.appendChild(form);
}
